/**
 * CountdownBanner - Banner que muestra countdown hasta el lanzamiento
 */

import React, { useState, useEffect } from 'react';
import { Rocket, Clock, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getTimeUntilRelease,
  formatReleaseDate,
  isV2Released,
  FEATURES,
} from '@/lib/featureFlags';

export default function CountdownBanner() {
  const [timeLeft, setTimeLeft] = useState(getTimeUntilRelease());
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Check if already dismissed this session
    const wasDismissed = sessionStorage.getItem('countdown_banner_dismissed');
    if (wasDismissed) {
      setDismissed(true);
      return;
    }
    
    // Update countdown every second
    const timer = setInterval(() => {
      setTimeLeft(getTimeUntilRelease());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const handleDismiss = () => {
    sessionStorage.setItem('countdown_banner_dismissed', 'true');
    setDismissed(true);
  };
  
  // Don't show if released or dismissed
  if (timeLeft.released || dismissed) {
    return null;
  }
  
  return (
    <div className="relative bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] border-b border-[#007AFF]/30 overflow-hidden">
      {/* Animated background effect */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
      <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#007AFF]/10 rounded-full blur-3xl" />
      <div className="absolute top-0 right-1/4 w-64 h-64 bg-[#34C759]/10 rounded-full blur-3xl" />
      
      <div className="relative max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Icon and Title */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex w-10 h-10 rounded-lg bg-gradient-to-br from-[#007AFF] to-[#34C759] items-center justify-center animate-pulse">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                <span className="font-semibold text-white text-sm sm:text-base">
                  AVX RF PRO v2.0
                </span>
              </div>
              <p className="text-xs text-[#71717A] hidden sm:block">
                Control RF en Red + Agente Local
              </p>
            </div>
          </div>
          
          {/* Center: Countdown */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Clock className="w-4 h-4 text-[#007AFF] hidden sm:block" />
            <div className="flex gap-1 sm:gap-2">
              <CountdownUnit value={timeLeft.days} label="días" />
              <span className="text-[#71717A] self-center">:</span>
              <CountdownUnit value={timeLeft.hours} label="hrs" />
              <span className="text-[#71717A] self-center">:</span>
              <CountdownUnit value={timeLeft.minutes} label="min" />
              <span className="text-[#71717A] self-center hidden sm:block">:</span>
              <CountdownUnit value={timeLeft.seconds} label="seg" className="hidden sm:flex" />
            </div>
          </div>
          
          {/* Right: Date and Close */}
          <div className="flex items-center gap-3">
            <div className="text-right hidden md:block">
              <p className="text-xs text-[#71717A]">Lanzamiento</p>
              <p className="text-sm font-medium text-[#34C759]">{formatReleaseDate()}</p>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-[#71717A] hover:text-white transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CountdownUnit({ value, label, className = '' }) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1 min-w-[36px] sm:min-w-[44px]">
        <span className="text-white font-mono font-bold text-sm sm:text-lg">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[8px] sm:text-[10px] text-[#52525B] mt-0.5">{label}</span>
    </div>
  );
}
